import { useCallback, useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { spassuCodeApiRef } from '../../api';
import { useAsyncOptions } from './useAsyncOptions';
import type { IssuePickerProps } from './IssuePicker';

export function useIssueSearch({ provider, scope }: IssuePickerProps) {
  const api = useApi(spassuCodeApiRef);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const search = useAsyncOptions(
    useCallback(async () => {
      const result = await api.searchIssues(provider, {
        scope: { owner: scope.owner, project: scope.project, repo: scope.repo },
        q: debouncedQuery || undefined,
        pageSize: 25,
      });
      return result.items;
    }, [api, provider, scope.owner, scope.project, scope.repo, debouncedQuery]),
    open,
  );

  // Debounce the query — every keystroke would otherwise hit the backend
  // and (for GitHub) burn through the 30 req/min unauthenticated search
  // rate-limit. 300ms is the same cadence used by the legacy WorkItemPicker.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  return { open, setOpen, query, setQuery, search };
}
export type IssueSearch = ReturnType<typeof useIssueSearch>;
